import React from 'react';
import { formatCompactNumber } from './format';
import { MembersAnalytics } from './membersAnalyticsLogic';
import { StepsAnalyticsIcon, WorkoutsAnalyticsIcon } from './membersAnalyticsIcon';

type CategorySummary = MembersAnalytics['categories'][number];
type BreakdownItem = CategorySummary['breakdown'][number];

interface IProps {
  category: CategorySummary;
  stepsTotal: number;
  studioWorkoutsTotal: number;
}

export default function BreakdownMetricCard(props: IProps) {
  const { category, stepsTotal, studioWorkoutsTotal } = props;

  return (
    <div className='members-analytics-interaction-card'>
      <div className='members-analytics-interaction-heading'>
        Interactions:
        <span className='members-analytics-footnote-marker'>**</span>
      </div>
      {category.id === 'exercise' ? (
        <div className='members-analytics-exercise-sections'>
          <ExerciseMetricSubsection
            icon={<StepsAnalyticsIcon className='members-analytics-subsection-icon' />}
            label='Steps'
            value={stepsTotal}
          />
          <div className='members-analytics-subsection'>
            <div className='members-analytics-subsection-heading'>
              <WorkoutsAnalyticsIcon className='members-analytics-subsection-icon' />
              <span>Workouts</span>
            </div>
            <BreakdownRows category={category} />
          </div>
          {studioWorkoutsTotal > 0 && (
            <ExerciseMetricSubsection
              icon={<WorkoutsAnalyticsIcon className='members-analytics-subsection-icon' />}
              label='Studio workouts'
              value={studioWorkoutsTotal}
            />
          )}
        </div>
      ) : (
        <BreakdownRows category={category} />
      )}
    </div>
  );
}

function ExerciseMetricSubsection({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className='members-analytics-subsection'>
      <div className='members-analytics-metric-row'>
        <div className='members-analytics-metric-label'>
          {icon}
          <span>{label}</span>
        </div>
        <div className='members-analytics-metric-value'>
          <strong>{formatCompactNumber(value)}</strong>
        </div>
      </div>
    </div>
  );
}

function BreakdownRows({ category }: { category: CategorySummary }) {
  const maxBreakdownCount = Math.max(...category.breakdown.map((item) => item.count), 1);

  return (
    <div className='members-analytics-breakdown'>
      {category.breakdown.length === 0 ? (
        <div className='members-analytics-muted'>No interactions</div>
      ) : category.breakdown.map((item, index) => {
        const barPercent = Math.max(8, Math.round((item.count / maxBreakdownCount) * 100));

        return (
          <div className='members-analytics-breakdown-row' key={`${category.id}-${item.label}-${index}`}>
            <div
              className='members-analytics-breakdown-bar'
              style={{ width: `${barPercent}%` }}
            />
            <div className='members-analytics-breakdown-value'>
              <strong>{formatCompactNumber(item.count)}</strong>
              <span className='members-analytics-breakdown-label'>
                {item.label}
                {shouldShowThumbsUp(category.id, item.label) && (
                  <img
                    className='members-analytics-breakdown-label-icon'
                    src='resources/images/members-analytics-thumbs-up.svg'
                    alt=''
                  />
                )}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function shouldShowThumbsUp(categoryId: string, label: BreakdownItem['label']): boolean {
  return categoryId === 'social' && (label === 'Sent' || label === 'Received');
}
